import {UI} from "../../../../../stem-core/ui/UIBase";

import {BasePanelPage, BasePanelPageStyle} from "../../../PanelPage";
import {Flow} from "../../../flows/Flow.js";
import {LoadingSpinner, wrapInSpinner} from "../../../../../core/ui/LoadingSpinner.jsx";
import {DonationAmountAndFrequency} from "./components/DonationAmountAndFrequency.jsx";
import {styleRule} from "../../../../../stem-core/decorators/Style";
import {registerStyle} from "../../../../../stem-core/ui/style/Theme";
import {Messages} from "../../../../Messages";
import {PaymentMethodAcceptOrChange} from "../payment-method/components/PaymentMethodAcceptOrChange.jsx";
import {AddPaymentMethodComponent} from "../payment-method/components/AddPaymentMethodComponent.jsx";
import {iFrameUserDataService} from "../../../../services/IFrameUserDataService";
import {AddNewOrLoadCardComponent} from "../payment-method/components/AddNewOrLoadCardComponent";
import {BlinkButton} from "../../../../../core/ui/Button.jsx";
import {BlinkInput} from "../../../../ui/Input.jsx";
import {authFormService} from "../../../../services/AuthFormService.js";
import {authService} from "../../../../../client/connection/services/AuthService.js";
import {AuthHelper} from "../../../../AuthHelper.js";
import {Router} from "../../../../../stem-core/ui/Router.jsx";
import {LOGIN_WITH_EMAIL_CODE_URL} from "../../../../Constants.js";
import {Toast} from "../../../../ui/Toast.jsx";
import {AnchoredPopup} from "../../../../../stem-core/ui/AnchoredPopup.jsx";
import {Direction} from "../../../../../stem-core/ui/Constants.js";


export class PaymentAndDonationPanelStyle extends BasePanelPageStyle {
    @styleRule
    paymentDetailsSection = {
        marginBottom: 24,
    };
}

@registerStyle(PaymentAndDonationPanelStyle)
export class PaymentAndDonationPanel extends BasePanelPage {
    forceAddCard = false;
    allowRedraw = true; // We don't want to redraw while waiting for results, to not randomly flash stuff
    extraConfirmationInterval = null;
    extraConfirmationAnchor = null;
    EXTRA_CHECK_MIN_HOLD_DURATION = 5000;

    isButtonDisabled() {
        return !Flow.donationDetails.isValid() || !this.paymentMethodInput?.isValid();
    }

    async ensureAuthenticated() {
        if (authService.isAuthenticated()) {
            return;
        }

        const {registrationMail} = authFormService;
        if (!registrationMail) {
            this.emailInputError = "Enter a valid email address";
            this.emailInput.updateOptions({error: this.emailInputError});
            throw this.emailInputError;
        }

        try {
            const requestPayload = {
                firstName: "",
                lastName: "",
                email: registrationMail,
                withSignup: true,
                secure: false,
            };

            await AuthHelper.requestLoginCode(requestPayload);
            if (!authService.isAuthenticated()) {
                // TODO @flow should be an await auth flow prompt that starts after sending the code
                Router.changeURL(LOGIN_WITH_EMAIL_CODE_URL);
                // TODO @flow this is dumb, since we might login as someone else. Should be right after this exact code is confirmed
                await authService.waitOnAuth();
            }
        } catch (error) {
            console.log(error);
            this.emailInputError = error.message;
            this.redraw();
        }
    }

    setDonationButtonPopup(content) {
        if (!content) {
            this.extraConfirmationAnchor?.hide();
            this.extraConfirmationAnchor = null;
            return;
        }
        if (!this.extraConfirmationAnchor?.isVisible()) {
            this.extraConfirmationAnchor = AnchoredPopup.show({
                anchor: this.donationButton,
                content,
                direction: Direction.UP,
            });
        } else {
            this.extraConfirmationAnchor.updateOptions({content});
        }
    }

    // Returns true if we held for long enough
    updateDurationHeld(durationHeld) {
        const durationNeeded = this.EXTRA_CHECK_MIN_HOLD_DURATION - durationHeld;
        if (durationNeeded > 0) {
            const numSecondsNeeded = Math.ceil(durationNeeded / 1000);
            this.setDonationButtonPopup(`Please hold for ${numSecondsNeeded} second(s) to confirm the larger donation.`)
            return;
        }

        // Not an infinite loop, since this won't call us
        this.clearExtraConfirmation(true);
    }

    clearExtraConfirmation(confirm = false) {
        if (!this.extraConfirmationInterval) {
            return;
        }
        clearInterval(this.extraConfirmationInterval);
        this.extraConfirmationInterval = null;

        if (confirm) {
            this.setDonationButtonPopup(() => "Release button to confirm donation");
            Flow.donationDetails.update({haveExtraConfirmation: true});
        } else {
            // Doing this in a timeout since the previous click might have closed the popup
            setTimeout(() => this.updateDurationHeld(0), 50);
        }
    }

    maybeStartExtraCheck() {
        if (!Flow.donationDetails.shouldDoubleCheckDonation()) {
            return;
        }
        Flow.donationDetails.update({haveExtraConfirmation: false});
        this.clearExtraConfirmation();

        const startPressTime = Date.now();
        this.extraConfirmationInterval = setInterval(() => {
            this.updateDurationHeld(Date.now() - startPressTime);
        }, 100);
    }

    @wrapInSpinner
    async donate() {
        if (Flow.donationDetails.shouldDoubleCheckDonation() && !Flow.donationDetails.haveExtraConfirmation) {
            this.clearExtraConfirmation();
            return;
        }

        this.allowRedraw = false;
        const isInitiallyAuthenticated = authService.isAuthenticated();
        Flow.paymentMethod.paymentError = null;
        if (!isInitiallyAuthenticated) {
            await this.paymentMethodInput.cardForm.prepareRequest();
            const currentPath = Router.getCurrentPath();
            await this.ensureAuthenticated();
            LoadingSpinner.show();
            try {
                await Flow.paymentMethod.sendUnprocessedNewToken();
            } catch (error) {
                // Get back to the previous URL
                Router.changeURL(currentPath);
                Toast.showError(error, "Failed to use card");
                return;
            }

            // Get back to the previous URL
            Router.changeURL(currentPath);
            LoadingSpinner.hide(true);
            LoadingSpinner.show();
        } else {
            // TODO @flow this shouldn't be required
            const paymentMethod = await this.paymentMethodInput.getPaymentMethod();
            Flow.paymentMethod.update({selectedValue: paymentMethod});
        }

        await Flow.paymentAndDonationDetails.updateAndResolve();
        LoadingSpinner.hide(true);

        this.allowRedraw = true;
        this.redraw();
    }

    redraw() {
        if (this.allowRedraw) {
            super.redraw();
        }
    }

    renderExistingPaymentMethodInput() {
        if (Flow.paymentMethod.selectedValue) {
            return <PaymentMethodAcceptOrChange ref="paymentMethodInput"/>
        }

        // TODO @cleanup test or remove this complexity
        // Maybe a payment method can be unlocked from a first party cookie access
        const firstPartyPaymentMethodsExist = (iFrameUserDataService.getExtraPaymentMethodCount() > 0 && !this.forceAddCard);

        return firstPartyPaymentMethodsExist && <AddNewOrLoadCardComponent
            onAddCard={() => {
                this.forceAddCard = true;
                this.redraw();
            }}/>;
    }

    renderPaymentMethodInput() {
        // Either we can pay with an existing payment method, or just show a new payment method input
        return this.renderExistingPaymentMethodInput() || <AddPaymentMethodComponent ref="paymentMethodInput" onChange={() => this.redraw()}/>;
    }

    renderEmailIfUnauth() {
        if (authService.isAuthenticated()) {
            return null;
        }
        const {emailInputError} = this;
        const {registrationMail} = authFormService;

        // TODO @flow this is just like AuthStartPanel, deduplicate

        const clearErrors = () => {
            this.emailInputError = null;
            this.emailInput.updateOptions({error: null});
        }

        return <div>
            <BlinkInput
                ref="emailInput"
                initialValue={registrationMail}
                error={emailInputError}
                onFocus={clearErrors}
                inputAttributes={{
                    type: "email",
                    testId: "authEmail",
                    placeholder: Messages.emailAddress
                }}
                onChange={() => {
                    authFormService.setRegistrationMail(this.emailInput.getValue());
                    clearErrors();
                }}
            />
        </div>
    }

    render() {
        const {styleSheet} = this;

        return [
            <div className={styleSheet.paymentDetailsSection}>
                <div className={styleSheet.title}>{Messages.paymentDetails}</div>
                {this.renderEmailIfUnauth()}
                {this.renderPaymentMethodInput()}
            </div>,
            <DonationAmountAndFrequency ref="donateComponent" onChange={() => this.redraw()}/>,

            <BlinkButton
                ref="donationButton"
                label={Flow.donationDetails.getDonateComponentButtonLabel()}
                disabled={this.isButtonDisabled()}
                onClick={() => this.donate()}
                onPressStart={() => this.maybeStartExtraCheck()}
                onPressStop={() => this.clearExtraConfirmation()}
                onMouseLeave={() => this.clearExtraConfirmation()}
                className={styleSheet.actionButton}
            />,
        ]
    }

    onMount() {
        super.onMount();
        this.redraw(); // TODO @flow This is for the first rendering, since they payment method input isn't properly configured
    }
}
